import { useState } from "react";
import { CheckCircle2, X, XCircle } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ReviewNotificationBanner — shows approved/rejected content reviews the ambassador
 * hasn't acknowledged yet. Dismissing marks all current items as seen on the server.
 */
export default function ReviewNotificationBanner({ items = [], onDismiss }) {
  const [hidden, setHidden] = useState(false);
  if (hidden || !items?.length) return null;

  const dismiss = async () => {
    try {
      await api.post("/ambassador/me/notifications/seen");
    } catch (err) {
      console.warn("Failed to mark notifications seen", err);
    }
    setHidden(true);
    onDismiss?.();
  };

  const approved = items.filter((i) => i.kind !== "referral" && i.kind !== "streak" && i.status === "approved");
  const rejected = items.filter((i) => i.kind !== "referral" && i.kind !== "streak" && i.status === "rejected");
  const referrals = items.filter((i) => i.kind === "referral");
  const streaks = items.filter((i) => i.kind === "streak");
  const totalPts =
    approved.reduce((s, i) => s + (i.points_awarded || 0), 0) +
    referrals.reduce((s, i) => s + (i.points_awarded || 0) + (i.conversion_bonus_awarded || 0), 0) +
    streaks.reduce((s, i) => s + (i.points_awarded || 0), 0);

  return (
    <div
      data-testid="review-notification-banner"
      className="relative overflow-hidden rounded-2xl border border-[#A855F7]/40 bg-gradient-to-br from-[#A855F7]/15 via-zinc-950 to-zinc-950 p-5"
    >
      <button
        onClick={dismiss}
        data-testid="banner-dismiss"
        className="absolute right-3 top-3 rounded-full p-1 text-zinc-500 hover:bg-zinc-900 hover:text-white"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">New updates</p>
      <p className="mt-1 font-display text-lg font-bold text-white">
        {approved.length > 0 && <>🎉 {approved.length} submission{approved.length > 1 ? "s" : ""} approved</>}
        {streaks.length > 0 && (
          <>
            {approved.length > 0 && <span className="text-zinc-500"> · </span>}
            🔥 {streaks.length === 1 ? `${streaks[0].weeks}-week streak!` : `${streaks.length} streak milestones`}
          </>
        )}
        {referrals.length > 0 && (
          <>
            {(approved.length > 0 || streaks.length > 0) && <span className="text-zinc-500"> · </span>}
            📈 {referrals.length} new signup{referrals.length > 1 ? "s" : ""}
          </>
        )}
        {totalPts > 0 && <span className="text-[#A855F7]"> · +{totalPts} pts</span>}
        {rejected.length > 0 && (
          <>
            {(approved.length > 0 || referrals.length > 0 || streaks.length > 0) && <span className="text-zinc-500"> · </span>}
            <span className="text-rose-300">{rejected.length} need revision</span>
          </>
        )}
      </p>
      <ul className="mt-3 space-y-2 text-sm" data-testid="banner-items">
        {items.slice(0, 5).map((it) => {
          if (it.kind === "streak") {
            return (
              <li key={it.id} className="flex items-start gap-2.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <span className="mt-0.5 text-base">{it.emoji || "🔥"}</span>
                <div className="min-w-0 flex-1 break-words">
                  <p>
                    <span className="font-semibold text-white">{it.weeks}-week streak unlocked!</span>{" "}
                    <span className="text-zinc-400">Keep the consistency going.</span>
                    {it.points_awarded > 0 && <span className="text-[#A855F7]"> · +{it.points_awarded} pts</span>}
                  </p>
                </div>
                <span className="shrink-0 font-mono text-[10px] text-zinc-600">
                  {new Date(it.at).toLocaleDateString()}
                </span>
              </li>
            );
          }
          if (it.kind === "referral") {
            const pts = (it.points_awarded || 0) + (it.conversion_bonus_awarded || 0);
            return (
              <li key={it.id} className="flex items-start gap-2.5 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2">
                <span className="mt-0.5 text-base">📈</span>
                <div className="min-w-0 flex-1 break-words">
                  <p>
                    <span className="font-semibold text-white">You drove a new signup{it.converted ? " · converted" : ""}</span>{" "}
                    <span className="text-zinc-400">on </span>
                    <span className="capitalize text-zinc-200">{(it.product_key || it.platform || "").replace(/-/g, " ")}</span>
                    {pts > 0 && <span className="text-[#A855F7]"> · +{pts} pts</span>}
                  </p>
                  {it.referred_email && <p className="mt-0.5 break-all text-xs text-zinc-500">{it.referred_email}</p>}
                </div>
                <span className="shrink-0 font-mono text-[10px] text-zinc-600">
                  {new Date(it.at).toLocaleDateString()}
                </span>
              </li>
            );
          }
          return (
            <li key={it.id} className="flex items-start gap-2.5 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2">
              {it.status === "approved" ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
              ) : (
                <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-300" />
              )}
              <div className="min-w-0 flex-1 break-words">
                <p className="capitalize">
                  <span className="font-semibold text-white">{it.content_type.replace(/_/g, " ")}</span>{" "}
                  <span className={it.status === "approved" ? "text-emerald-300" : "text-rose-300"}>
                    {it.status === "approved" ? `· +${it.points_awarded} pts` : "· rejected"}
                  </span>
                </p>
                {it.rejection_reason && <p className="mt-0.5 text-xs text-zinc-400">Reason: {it.rejection_reason}</p>}
              </div>
              <span className="shrink-0 font-mono text-[10px] text-zinc-600">
                {new Date(it.at || it.reviewed_at).toLocaleDateString()}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
